// Admin Settings Tab Component
import React, { useState, useRef } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Key, Mail, Send, Bell, Clock, Shield, Eye, EyeOff, RefreshCw, Upload, Trash2, QrCode, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin, API_URL } from './AdminContext';

export function AdminSettingsTab() {
  const { platformSettings, setPlatformSettings, fetchPlatformSettings } = useAdmin();
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [generatingVapid, setGeneratingVapid] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const qrFileInputRef = useRef(null);
  
  // Password visibility states
  const [showPasswords, setShowPasswords] = useState({
    razorpay_secret: false,
    smtp_password: false,
    vapid_private: false
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed for QR code');
      return;
    }
    
    setUploadingQr(true);
    try {
      const formData = new FormData();
      formData.append('qr_code', file);
      
      const response = await fetch(`${API_URL}/api/platform-settings/bank-details/upload-qr`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        toast.success('QR code uploaded successfully');
        fetchPlatformSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload QR code');
      }
    } catch (error) {
      toast.error('Failed to upload QR code');
    }
    setUploadingQr(false);
    if (qrFileInputRef.current) {
      qrFileInputRef.current.value = '';
    }
  };

  const handleQrDelete = async () => {
    if (!confirm('Are you sure you want to delete the QR code?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/platform-settings/bank-details/qr-code`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('QR code deleted');
        fetchPlatformSettings();
      } else {
        toast.error('Failed to delete QR code');
      }
    } catch (error) {
      toast.error('Failed to delete QR code');
    }
  };

  const saveSettings = async (section, data) => {
    setSettingsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/platform-settings/${section}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      if (response.ok) {
        toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved`);
        fetchPlatformSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSettingsSaving(false);
  };

  const testEmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform-settings/test-email`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const generateVapidKeys = async () => {
    setGeneratingVapid(true);
    try {
      const response = await fetch(`${API_URL}/api/platform-settings/web-push/generate-vapid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('VAPID keys generated successfully!');
        // Refresh settings to show new keys
        await fetchPlatformSettings();
      } else {
        toast.error(data.detail || 'Failed to generate VAPID keys');
      }
    } catch (error) {
      console.error('VAPID generation error:', error);
      toast.error('Failed to generate VAPID keys');
    }
    setGeneratingVapid(false);
  };

  return (
    <div className="space-y-4">
      {/* Payment Gateway Settings */}
      <GroupBox title="Payment Gateway" className="border-green-500/50">
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Provider</Label>
              <Select 
                value={platformSettings?.payment_gateway?.provider || "razorpay"}
                onValueChange={(v) => setPlatformSettings({
                  ...platformSettings,
                  payment_gateway: { ...platformSettings?.payment_gateway, provider: v }
                })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="razorpay">Razorpay</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch 
                checked={platformSettings?.payment_gateway?.is_live_mode || false}
                onCheckedChange={(checked) => setPlatformSettings({
                  ...platformSettings,
                  payment_gateway: { ...platformSettings?.payment_gateway, is_live_mode: checked }
                })}
              />
              <Label className="text-sm">{platformSettings?.payment_gateway?.is_live_mode ? 'Live Mode' : 'Test Mode'}</Label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Razorpay Key ID</Label>
              <Input 
                value={platformSettings?.payment_gateway?.razorpay_key_id || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  payment_gateway: { ...platformSettings?.payment_gateway, razorpay_key_id: e.target.value }
                })}
                placeholder="rzp_test_..."
              />
            </div>
            <div>
              <Label className="text-sm">Razorpay Key Secret</Label>
              <div className="relative flex">
                <Input 
                  type={showPasswords.razorpay_secret ? "text" : "password"}
                  value={platformSettings?.payment_gateway?.razorpay_key_secret || ''}
                  onChange={(e) => setPlatformSettings({
                    ...platformSettings,
                    payment_gateway: { ...platformSettings?.payment_gateway, razorpay_key_secret: e.target.value }
                  })}
                  placeholder="Enter new secret to update"
                  className="pr-12"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-700 transition-colors"
                  onClick={() => togglePasswordVisibility('razorpay_secret')}
                  title={showPasswords.razorpay_secret ? "Hide" : "Show"}
                >
                  {showPasswords.razorpay_secret ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              {platformSettings?.payment_gateway?.razorpay_key_secret?.startsWith('••••') && (
                <p className="text-xs text-green-500 mt-1">✓ Secret key is saved (showing masked value)</p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={settingsSaving} onClick={() => saveSettings('payment-gateway', platformSettings?.payment_gateway)}>
              <Key className="w-4 h-4 mr-2" />
              Save Payment Settings
            </Button>
          </div>
        </div>
      </GroupBox>

      {/* Bank Details for Manual Payments */}
      <GroupBox title="Bank Details (for Manual Payments)" className="border-amber-500/50">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Switch 
                checked={platformSettings?.bank_details?.is_enabled || false}
                onCheckedChange={(checked) => setPlatformSettings({
                  ...platformSettings,
                  bank_details: { ...platformSettings?.bank_details, is_enabled: checked }
                })}
              />
              <Label className="text-sm">Enable Manual Payment Option</Label>
            </div>
            <p className="text-xs text-muted-foreground">Show bank/UPI details on checkout when payment gateway fails</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Bank Name</Label>
              <Input 
                value={platformSettings?.bank_details?.bank_name || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  bank_details: { ...platformSettings?.bank_details, bank_name: e.target.value }
                })}
                placeholder="e.g., HDFC Bank"
              />
            </div>
            <div>
              <Label className="text-sm">Account Holder Name</Label>
              <Input 
                value={platformSettings?.bank_details?.account_name || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  bank_details: { ...platformSettings?.bank_details, account_name: e.target.value }
                })}
                placeholder="e.g., YellowGold Pro"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Account Number</Label>
              <Input 
                value={platformSettings?.bank_details?.account_number || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  bank_details: { ...platformSettings?.bank_details, account_number: e.target.value }
                })}
                placeholder="e.g., 1234567890"
              />
            </div>
            <div>
              <Label className="text-sm">IFSC Code</Label>
              <Input 
                value={platformSettings?.bank_details?.ifsc_code || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  bank_details: { ...platformSettings?.bank_details, ifsc_code: e.target.value }
                })}
                placeholder="e.g., HDFC0001234"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">UPI ID</Label>
              <Input 
                value={platformSettings?.bank_details?.upi_id || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  bank_details: { ...platformSettings?.bank_details, upi_id: e.target.value }
                })}
                placeholder="e.g., yellowgold@upi"
              />
            </div>
            <div>
              <Label className="text-sm">Payment QR Code</Label>
              {platformSettings?.bank_details?.qr_code_url ? (
                <div className="mt-2 space-y-2">
                  <div className="relative inline-block">
                    <img 
                      src={platformSettings.bank_details.qr_code_url} 
                      alt="Payment QR Code" 
                      className="w-32 h-32 object-contain rounded-lg border bg-white p-1"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={handleQrDelete}
                      title="Delete QR Code"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-xs text-green-500">✓ QR code uploaded</p>
                </div>
              ) : (
                <div className="mt-2">
                  <input 
                    type="file"
                    ref={qrFileInputRef}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleQrUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => qrFileInputRef.current?.click()}
                    disabled={uploadingQr}
                    className="w-full"
                  >
                    {uploadingQr ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload QR Code
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF. Max 10MB</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button size="sm" disabled={settingsSaving} onClick={() => saveSettings('bank-details', platformSettings?.bank_details)}>
              <Key className="w-4 h-4 mr-2" />
              Save Bank Details
            </Button>
          </div>
        </div>
      </GroupBox>

      {/* Email Settings */}
      <GroupBox title="Email Configuration" className="border-blue-500/50">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Switch 
                checked={platformSettings?.email?.is_enabled || false}
                onCheckedChange={(checked) => setPlatformSettings({
                  ...platformSettings,
                  email: { ...platformSettings?.email, is_enabled: checked }
                })}
              />
              <Label>Email Notifications Enabled</Label>
            </div>
            <Select 
              value={platformSettings?.email?.provider || "smtp"}
              onValueChange={(v) => setPlatformSettings({
                ...platformSettings,
                email: { ...platformSettings?.email, provider: v }
              })}
            >
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="smtp">Gmail SMTP</SelectItem>
                <SelectItem value="sendgrid">SendGrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">SMTP Host</Label>
              <Input 
                value={platformSettings?.email?.smtp_host || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  email: { ...platformSettings?.email, smtp_host: e.target.value }
                })}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <Label className="text-sm">SMTP Port</Label>
              <Input 
                type="number"
                value={platformSettings?.email?.smtp_port || 587}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  email: { ...platformSettings?.email, smtp_port: parseInt(e.target.value) }
                })}
              />
            </div>
            <div>
              <Label className="text-sm">Username</Label>
              <Input 
                value={platformSettings?.email?.smtp_username || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  email: { ...platformSettings?.email, smtp_username: e.target.value }
                })}
                placeholder="your@gmail.com"
              />
            </div>
            <div>
              <Label className="text-sm">Password / App Password</Label>
              <div className="relative flex">
                <Input 
                  type={showPasswords.smtp_password ? "text" : "password"}
                  value={platformSettings?.email?.smtp_password || ''}
                  onChange={(e) => setPlatformSettings({
                    ...platformSettings,
                    email: { ...platformSettings?.email, smtp_password: e.target.value }
                  })}
                  placeholder="Enter new password to update"
                  className="pr-12"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-700 transition-colors"
                  onClick={() => togglePasswordVisibility('smtp_password')}
                  title={showPasswords.smtp_password ? "Hide" : "Show"}
                >
                  {showPasswords.smtp_password ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              {platformSettings?.email?.smtp_password?.startsWith('••••') && (
                <p className="text-xs text-green-500 mt-1">✓ Password is saved (showing masked value)</p>
              )}
            </div>
            <div>
              <Label className="text-sm">From Email</Label>
              <Input 
                value={platformSettings?.email?.from_email || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  email: { ...platformSettings?.email, from_email: e.target.value }
                })}
                placeholder="noreply@yellowgoldpro.com"
              />
            </div>
            <div>
              <Label className="text-sm">From Name</Label>
              <Input 
                value={platformSettings?.email?.from_name || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  email: { ...platformSettings?.email, from_name: e.target.value }
                })}
                placeholder="YellowGold Pro"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={testEmail}>
              <Send className="w-4 h-4 mr-2" />
              Send Test Email
            </Button>
            <Button size="sm" disabled={settingsSaving} onClick={() => saveSettings('email', platformSettings?.email)}>
              <Mail className="w-4 h-4 mr-2" />
              Save Email Settings
            </Button>
          </div>
        </div>
      </GroupBox>

      {/* Web Push Settings */}
      <GroupBox title="Web Push Notifications" className="border-purple-500/50">
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Switch 
              checked={platformSettings?.web_push?.is_enabled || false}
              onCheckedChange={(checked) => setPlatformSettings({
                ...platformSettings,
                web_push: { ...platformSettings?.web_push, is_enabled: checked }
              })}
            />
            <Label>Web Push Enabled</Label>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">VAPID Public Key</Label>
              <Input 
                value={platformSettings?.web_push?.vapid_public_key || ''}
                readOnly
                className="bg-muted text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-sm">VAPID Private Key</Label>
              <div className="relative flex">
                <Input 
                  type={showPasswords.vapid_private ? "text" : "password"}
                  value={platformSettings?.web_push?.vapid_private_key || ''}
                  readOnly
                  className="bg-muted pr-12 text-xs font-mono"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-700 transition-colors"
                  onClick={() => togglePasswordVisibility('vapid_private')}
                  title={showPasswords.vapid_private ? "Hide" : "Show"}
                >
                  {showPasswords.vapid_private ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              {platformSettings?.web_push?.vapid_private_key?.startsWith('••••') && (
                <p className="text-xs text-green-500 mt-1">✓ Private key is saved (click Generate to create new keys)</p>
              )}
            </div>
            <div>
              <Label className="text-sm">VAPID Email</Label>
              <Input 
                value={platformSettings?.web_push?.vapid_email || ''}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  web_push: { ...platformSettings?.web_push, vapid_email: e.target.value }
                })}
                placeholder="admin@yellowgoldpro.com"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={generateVapidKeys}
              disabled={generatingVapid}
            >
              {generatingVapid ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              {generatingVapid ? 'Generating...' : 'Generate New VAPID Keys'}
            </Button>
            <Button size="sm" disabled={settingsSaving} onClick={() => saveSettings('web-push', platformSettings?.web_push)}>
              <Bell className="w-4 h-4 mr-2" />
              Save Push Settings
            </Button>
          </div>
        </div>
      </GroupBox>

      {/* Auto Backup Settings */}
      <GroupBox title="Auto Backup Configuration" className="border-yellow-500/50">
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Switch 
              checked={platformSettings?.auto_backup?.is_enabled || false}
              onCheckedChange={(checked) => setPlatformSettings({
                ...platformSettings,
                auto_backup: { ...platformSettings?.auto_backup, is_enabled: checked }
              })}
            />
            <Label>Auto Backup Enabled</Label>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Frequency</Label>
              <Select 
                value={platformSettings?.auto_backup?.frequency || "daily"}
                onValueChange={(v) => setPlatformSettings({
                  ...platformSettings,
                  auto_backup: { ...platformSettings?.auto_backup, frequency: v }
                })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Time (24h)</Label>
              <Input 
                value={platformSettings?.auto_backup?.backup_time || '02:00'}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  auto_backup: { ...platformSettings?.auto_backup, backup_time: e.target.value }
                })}
                placeholder="02:00"
              />
            </div>
            <div>
              <Label className="text-sm">Retention (days)</Label>
              <Input 
                type="number"
                value={platformSettings?.auto_backup?.retention_days || 30}
                onChange={(e) => setPlatformSettings({
                  ...platformSettings,
                  auto_backup: { ...platformSettings?.auto_backup, retention_days: parseInt(e.target.value) }
                })}
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button size="sm" disabled={settingsSaving} onClick={() => saveSettings('auto-backup', platformSettings?.auto_backup)}>
              <Clock className="w-4 h-4 mr-2" />
              Save Backup Settings
            </Button>
          </div>
        </div>
      </GroupBox>

      {/* Scheduler Settings */}
      <GroupBox title="Background Jobs & Scheduler" className="border-gray-500/50">
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Switch 
              checked={platformSettings?.scheduler?.is_enabled || false}
              onCheckedChange={(checked) => setPlatformSettings({
                ...platformSettings,
                scheduler: { ...platformSettings?.scheduler, is_enabled: checked }
              })}
            />
            <Label>Background Jobs Enabled</Label>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Switch 
                checked={platformSettings?.scheduler?.subscription_check_enabled || false}
                onCheckedChange={(checked) => setPlatformSettings({
                  ...platformSettings,
                  scheduler: { ...platformSettings?.scheduler, subscription_check_enabled: checked }
                })}
              />
              <Label className="text-sm">Subscription Expiry Check</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={platformSettings?.scheduler?.email_reminders_enabled || false}
                onCheckedChange={(checked) => setPlatformSettings({
                  ...platformSettings,
                  scheduler: { ...platformSettings?.scheduler, email_reminders_enabled: checked }
                })}
              />
              <Label className="text-sm">Email Reminders</Label>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button size="sm" disabled={settingsSaving} onClick={() => saveSettings('scheduler', platformSettings?.scheduler)}>
              <Shield className="w-4 h-4 mr-2" />
              Save Scheduler Settings
            </Button>
          </div>
        </div>
      </GroupBox>
    </div>
  );
}
